/**
 * useVoiceSession — browser-side voice client for the Nadia voice agent.
 *
 * Switched from native WebRTC to Pipecat's PipecatClient + WebSocket
 * transport so we can sidestep ICE/STUN/UDP routing complexity:
 * WebSocket is plain TCP on the same port we already serve HTTP on,
 * so it goes through any firewall or proxy that already passes our
 * HTTP traffic.
 *
 * Flow per call:
 *   1. POST /v1/nadia2/conversations/{id}/voice/tickets → {ticket, ws_url}
 *   2. Open PipecatClient with WebSocketTransport pointing at
 *      "${ws_url}?ticket=${ticket}"
 *   3. The transport handles mic capture and audio playback; we just
 *      listen for state changes.
 *
 * Media manager: we pass an explicit WavMediaManager (Web Audio API via
 * wavtools) instead of the transport's default DailyMediaManager. The Daily
 * manager pulls in @daily-co/daily-js, which fetches a "call machine" bundle
 * from c.daily.co at runtime — blocked by our CSP and unnecessary, since our
 * transport is a plain WebSocket and we never join a Daily room. WavMediaManager
 * is fully self-contained (its audio worklet is an inlined blob, no CDN). Note:
 * it captures at the rate we pass but *plays back at a fixed 24 kHz*, so the
 * voice server's audio_out_sample_rate must be 24 kHz (see nadia_voice.agent).
 */

import { logger, LogLevel, PipecatClient, type RTVIMessage } from "@pipecat-ai/client-js";
import { ProtobufFrameSerializer, WavMediaManager, WebSocketTransport } from "@pipecat-ai/websocket-transport";
import { fetchApi } from "~vue/api";
import { type Ref, ref } from "vue";

// Verbose Pipecat logging in the browser console — invaluable for
// diagnosing handshake / audio-flow issues during the prototype phase.
// Trim to LogLevel.WARN once the feature stabilises.
logger.setLevel(LogLevel.DEBUG);

interface VoiceTicketResponse {
    ticket: string;
    ws_url: string;
    expires_at: string;
}

export type VoiceState = "idle" | "connecting" | "live" | "ending" | "error";

// Live event delivered from the voice agent via RTVI server-message. Mirrors
// the shape text-mode receives via SSE: ``{event, data}``. nadia_voice wraps
// each coach_api SSE frame in ``{kind: "nadia2_event", event, data}`` before
// pushing it as a server-message; we strip the discriminator here.
export interface VoiceStreamEvent {
    event: string;
    data: unknown;
}

export interface VoiceSessionCallbacks {
    onStreamEvent?: (event: VoiceStreamEvent) => void;
}

export interface VoiceSession {
    state: Ref<VoiceState>;
    error: Ref<string | null>;
    start: (conversationId: string) => Promise<void>;
    stop: () => Promise<void>;
}

// Mic capture rate — matches the server-side audio_in_sample_rate in
// nadia_voice.agent. 16 kHz is the OpenAI Whisper/transcription sweet spot.
// (Playback is fixed at 24 kHz by WavMediaManager; the server sends bot audio
// at 24 kHz to match — see audio_out_sample_rate in nadia_voice.agent.)
const MIC_SAMPLE_RATE = 16_000;

// Shared with MicTester.vue so the mic the user verified in the test
// dialog is the same mic Pipecat will capture from on the live call.
export const PREFERRED_MIC_KEY = "nadia2_voice_mic_id";

export function useVoiceSession(callbacks: VoiceSessionCallbacks = {}): VoiceSession {
    const state = ref<VoiceState>("idle");
    const error = ref<string | null>(null);

    let client: PipecatClient | null = null;

    const teardown = async () => {
        if (client) {
            try {
                await client.disconnect();
            } catch {
                // ignore — we're tearing down anyway
            }
            client = null;
        }
    };

    const start = async (conversationId: string): Promise<void> => {
        if (state.value !== "idle") {
            return;
        }
        state.value = "connecting";
        error.value = null;

        try {
            const ticketRes = await fetchApi(`/v1/nadia2/conversations/${conversationId}/voice/tickets`, {
                method: "POST",
            });
            if (!ticketRes.ok) {
                throw new Error(`ticket fetch failed: ${ticketRes.status}`);
            }
            const ticket = (await ticketRes.json()) as VoiceTicketResponse;

            const transport = new WebSocketTransport({
                serializer: new ProtobufFrameSerializer(),
                // Daily-free media manager (see file header). First arg is the
                // recorder chunk size (default); second is the mic capture rate.
                // Playback is fixed at 24 kHz inside WavMediaManager.
                mediaManager: new WavMediaManager(undefined, MIC_SAMPLE_RATE),
            });

            client = new PipecatClient({
                transport,
                enableMic: true,
                enableCam: false,
                callbacks: {
                    onConnected: () => {
                        // Fired when the WebSocket is open and the media
                        // manager (mic) has connected, BEFORE bot-ready.

                        console.log("[voice] onConnected — WS open + mic ready");
                    },
                    onBotReady: (data) => {
                        // Server's RTVI handshake response. Only after
                        // this does audio actually start flowing both
                        // ways. Treat this as the real "live" signal.

                        console.log("[voice] onBotReady", data);
                        state.value = "live";
                    },
                    onUserStartedSpeaking: () => {
                        console.log("[voice] user started speaking");
                    },
                    onUserStoppedSpeaking: () => {
                        console.log("[voice] user stopped speaking");
                    },
                    onBotStartedSpeaking: () => {
                        console.log("[voice] bot started speaking");
                    },
                    onUserTranscript: (data) => {
                        // Pipecat fires this as soon as STT returns each
                        // segment (Whisper REST: ~1.5s after VAD stop;
                        // streaming STT: word-by-word). Forward to the chat
                        // UI so the user's bubble fills in immediately
                        // instead of waiting for coach_api to echo the
                        // persisted user_message ~1-2s later.
                        if (!data || !("text" in data)) {
                            return;
                        }
                        const final = "final" in data ? Boolean((data as { final?: boolean }).final) : true;
                        callbacks.onStreamEvent?.({
                            event: "user_transcript",
                            data: { text: String((data as { text: string }).text), final },
                        });
                    },
                    onBotTranscript: (data) => {
                        console.log("[voice] bot transcript", data);
                    },
                    onServerMessage: (raw) => {
                        // nadia_voice wraps every coach_api SSE event into
                        // {kind: "nadia2_event", event, data}. Forward it as
                        // a VoiceStreamEvent so the chat UI can stream the
                        // turn the same way text mode does.
                        if (raw && typeof raw === "object" && "kind" in raw && (raw as { kind: string }).kind === "nadia2_event") {
                            const payload = raw as { event: string; data: unknown };
                            callbacks.onStreamEvent?.({ event: payload.event, data: payload.data });
                            return;
                        }
                        console.debug("[voice] server message", raw);
                    },
                    onDisconnected: () => {
                        console.log("[voice] onDisconnected");
                        state.value = "idle";
                        client = null;
                    },
                    onError: (msg: RTVIMessage) => {
                        // Pipecat surfaces errors as RTVIMessage with the
                        // human-readable detail in `data` (typically
                        // {message: string} or a plain string).
                        const detail =
                            typeof msg === "string"
                                ? msg
                                : msg?.data && typeof msg.data === "object" && "message" in msg.data
                                  ? String((msg.data as { message: unknown }).message)
                                  : JSON.stringify(msg?.data ?? msg);

                        console.error("[voice] onError", msg);
                        state.value = "error";
                        error.value = detail;
                    },
                },
            });

            // Append the JWT as a query param. The voice agent reads it
            // off the upgrade request and validates before accepting.
            const sep = ticket.ws_url.includes("?") ? "&" : "?";
            const wsUrl = `${ticket.ws_url}${sep}ticket=${encodeURIComponent(ticket.ticket)}`;
            await client.connect({ wsUrl });

            // After the transport is up, switch to the mic the user
            // picked in the test dialog (if any). PipecatClientOptions
            // has no deviceId field, so post-connect updateMic is the
            // supported path. Any failure is non-fatal — the call is
            // already running on the system default mic.
            const savedMicId = localStorage.getItem(PREFERRED_MIC_KEY);
            if (savedMicId) {
                try {
                    client.updateMic(savedMicId);
                    console.log("[voice] applied saved mic", savedMicId);
                } catch (micErr) {
                    console.warn("[voice] updateMic failed", micErr);
                }
            }
        } catch (err) {
            state.value = "error";
            error.value = err instanceof Error ? err.message : String(err);
            await teardown();
        }
    };

    const stop = async (): Promise<void> => {
        if (state.value === "idle") {
            return;
        }
        state.value = "ending";
        await teardown();
        state.value = "idle";
    };

    return { state, error, start, stop };
}
